<script lang="ts">import { Background } from "@infinitro/background";
import { Card } from "@infinitro/card";
import { toGameSlug, toProviderSlug } from "@infinitro/catalog-min";
import { cfgBrandTitle, cfgCdnBase, cfgToThemeContentPath, FeatureSet, ReleaseStage, stateFeatureSet, stateReleaseStage, stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { locale, loggedIn, maskUsername, providerBasePath, toSiteUrl } from "@infinitro/global";
import { InfoCard } from "@infinitro/info-card";
import { ArrowIcon } from "@infinitro/icons";
import { Text } from "@infinitro/text";
import { Tabs } from "@infinitro/tabs";
import { goto } from "$app/navigation";
import MarkdownIt from "markdown-it";
import { onMount } from "svelte";
import AverageRating from "./review/average-rating.svelte";
import Comments from "./review/comments.svelte";
import RateGame from "./review/rate-game.svelte";
const CDN_BASE = cfgCdnBase();
const md = new MarkdownIt();
export let original = true;
export let game;
export let dropdownOpen = false;
let content;
let htmlContent = "";
let fetchContentError = false;
let activeTab = "description";
let hasDescription = false;
let reviewRefreshKey = 0;
// Check if review system should be available (Preview mode only)
$: isPreviewMode = $stateReleaseStage === ReleaseStage.Alpha && $stateFeatureSet === FeatureSet.Preview;
$: maskUsernames = Boolean($stateUi.profileModal.maskUsernames);
const displayBiggestWinUsername = (username) => {
	if (!username) return "";
	return maskUsernames ? maskUsername(username) : username;
};
$: infoAccordionTabs = hasDescription && isPreviewMode ? [{
	label: $t(Verbiage.description) || "Description",
	value: "description"
}, {
	label: $t(Verbiage.reviews_tab) || "Review",
	value: "review"
}] : [];
function handleReviewUpdate(event) {
	reviewRefreshKey += 1;
}
function handleInfoAccordionTabChange(detail) {
	activeTab = String(detail.value);
}
async function fetchContent() {
	try {
		const response = await fetch(content);
		if (!response.ok) {
			fetchContentError = true;
			hasDescription = false;
			// If no description and in preview mode, default to review tab
			if (isPreviewMode) {
				activeTab = "review";
			}
			throw new Error(`Error fetching content: ${response.statusText}`);
		}
		let text = await response.text();
		text = text.replace(/%(domain|brand)%/g, cfgBrandTitle());
		htmlContent = md.render(text).replace(/<\/?h1(\s|>)/g, (m) => m.replace("h1", "h2"));
		htmlContent = htmlContent.replace(new RegExp("<p>", "g"), "<p style=\"font-weight: 400\">");
		htmlContent = htmlContent.replace(new RegExp("<li>", "g"), "<li style=\"font-weight: 400\">");
		htmlContent = htmlContent.replace(new RegExp("<table>", "g"), "<div style=\"overflow-x: auto; margin-bottom: 5px\"><table>");
		htmlContent = htmlContent.replace(new RegExp("</table>", "g"), "</table></div>");
		// If we successfully fetched content, we have a description
		hasDescription = true;
	} catch (error) {
		fetchContentError = true;
		hasDescription = false;
		// If no description and in preview mode, default to review tab
		if (isPreviewMode) {
			activeTab = "review";
		}
		htmlContent = "";
	}
}
$: {
	if (original && game?.game) {
		const providerPathSlug = game.provider_slug || toProviderSlug(game.prov ?? "");
		if (providerPathSlug) {
			const mdSlug = game.game_slug || toGameSlug(game.game);
			// Dedicated description file, separate from the SEO copy the GameSeoText panel renders,
			// so the two don't duplicate. None exist yet → 404 → hasDescription=false.
			content = cfgToThemeContentPath(`/game-descriptions/${mdSlug}/${mdSlug}.${$locale}.md`);
			fetchContent();
		}
	}
}
$: gameName = $locale === "ja" ? game.ja ?? game.game : $locale === "ko" ? game.ko ?? game.game : game.game;
$: providerName = game.prov?.trim() ?? "";
$: h1GameName = !$loggedIn ? providerName ? `${$t(Verbiage.play) || "Play"} ${gameName} ${$t(Verbiage.by) || "by"} ${providerName}` : `${$t(Verbiage.play) || "Play"} ${gameName}` : gameName;
const toggleDropdown = () => {
	if (!original) return;
	// Don't allow opening if there's no description and not in preview mode
	if (!hasDescription && !isPreviewMode) return;
	dropdownOpen = !dropdownOpen;
};
function volatilityColor(volatility) {
	switch (volatility) {
		case "high": return "#FF9E14";
		case "medium": return "#FFFFFF";
		case "low": return "#3EFFB4";
		default: return "#FFFFFF";
	}
}
function payoutColor(payout) {
	if (payout === 0) {
		return "#FFFFFF";
	} else if (payout > 0) {
		return "#3EFFB4";
	} else {
		return "#FF9E14";
	}
}
onMount(() => {
	if (original) {
		fetchContent();
	}
});
$: providerPath = game?.prov ? `${providerBasePath(game.prov)}${toProviderSlug(game.provider_slug || game.prov)}/` : null;
$: infoAccordionUi = $stateUi?.infoAccordion;
$: providerPageHref = !!infoAccordionUi?.showProviderLink && providerPath ? toSiteUrl(providerPath, $loggedIn) : null;
function handleProviderLinkClick(event) {
	event.preventDefault();
	event.stopPropagation();
	if (!providerPageHref) return;
	goto(providerPageHref);
}
</script>

{#if game}
	<div class="custom-dropdown {dropdownOpen ? 'custom-dropdown-open' : ''}">
		<!-- svelte-ignore a11y-click-events-have-key-events -->
		<!-- svelte-ignore a11y-no-static-element-interactions -->

		<div class="dropdown-toggle pointer" on:click={toggleDropdown}>
			<div class="d-flex-col title-name">
				<Text className="info-accordion-game-name-text" type="title" titleSize="h1"
					>{h1GameName}</Text
				>
				{#if providerName}
					<Text type="body" bodySize="medium">
						{$t(Verbiage.by)}{' '}
						{#if providerPageHref}
							<a
								href={providerPageHref}
								class="bold-text provider-page-link"
								rel="noopener noreferrer"
								on:click={handleProviderLinkClick}
							>
								{providerName}
							</a>
						{:else}
							<span class="bold-text">{providerName}</span>
						{/if}
					</Text>
				{/if}
			</div>
			<div class="d-flex-col title-name-mobile">
				<Text type="subtitle" subtitleSize="medium" subtitleWeight="bold">{gameName}</Text>
				{#if providerName}
					<Text type="body" bodySize="medium">
						{$t(Verbiage.by)}{' '}
						{#if providerPageHref}
							<a
								href={providerPageHref}
								class="bold-text provider-page-link"
								rel="noopener noreferrer"
								on:click={handleProviderLinkClick}
							>
								{providerName}
							</a>
						{:else}
							<span class="bold-text">{providerName}</span>
						{/if}
					</Text>
				{/if}
			</div>
			{#if original && (hasDescription || isPreviewMode)}
				<div class="chevron-container" class:rotated={dropdownOpen}>
					<ArrowIcon />
				</div>
			{/if}
			<!-- <div class="d-flex-end">
                {#if game.biggestWin}
                    <div class="biggest-win">
                        <Background
                            small={true}
                            input={true}
                            noGradient={true}
                            noFigures={true}
                            rounded={true}
                        />
                        <img src={`${CDN_BASE}/images/misc/money-hat.png`} alt={$t(Verbiage.icon_alt, 'alts')} />
                        <Text type="body" bodyWeight="bold" customStyle="color: #FF9E14"
                            >{game.biggestWin.payout}x</Text
                        >
                        <Text type="body" bodyWeight="bold">{displayBiggestWinUsername(game.biggestWin.username)}</Text>
                    </div>
                {/if}
                {#if original}
                    <div class:rotated={dropdownOpen}>
                        <ArrowIcon />
                    </div>
                {/if}
            </div> -->
		</div>
		<div class:expanded={dropdownOpen} class="dropdown-menu">
			<div class="game-content">
				<!-- <Background
					medium={true}
					noGradient={true}
					noFigures={true}
					rounded={true}
					zIndex={20}
					roundedXl
					starQuantity={50}
					maxSizeStar={10}
				/> -->
				<div class="game-description">
					{#if hasDescription && isPreviewMode}
						<Tabs
							tabs={infoAccordionTabs}
							{activeTab}
							onChange={handleInfoAccordionTabChange}
							width="100%"
							size="small"
							shouldScrollIntoView={false}
							customClass="info-accordion-tab-strip"
						/>
					{/if}

					<!-- <div class="game-data">
							{#if game.tags}
                                <div class="game-tags">
                                    {#each game.tags as tag}
                                        <Tag {tag} />
                                    {/each}
                                </div>
                            {/if}
							<div class="game-stats {!game.tags && 'game-stats-full'}">
                                <div class="game-stats-item">
                                    <div class="game-stats-column">
                                        <Text bodyWeight="bold">{$t(Verbiage.bonus_buys)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.reel_design)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.lucky_spin)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.win_ways)}</Text>
                                    </div>
                                    <div class="game-stats-column game-stats-column-end">
                                        <Text bodyWeight="bold">Yes</Text>
                                        <Text bodyWeight="bold">5x5</Text>
                                        <Text bodyWeight="bold">Yes</Text>
                                        <Text bodyWeight="bold">Cluster Pays</Text>
                                    </div>
                                </div>
                                <div class="game-stats-item">
                                    <div class="game-stats-column">
                                        <Text bodyWeight="bold">{$t(Verbiage.max_win)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.hit_rate)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.volatility)}</Text>
                                        <Text bodyWeight="bold">{$t(Verbiage.stakes_range)}</Text>
                                    </div>
                                    <div class="game-stats-column game-stats-column-end">
                                        <Text
                                            bodyWeight="bold"
                                            customStyle={`color: ${payoutColor(game.biggestWin?.payout || 0)}`}
                                            >1500.65x</Text
                                        >
                                        <Text bodyWeight="bold">18.55%</Text>
                                        <Text bodyWeight="bold" customStyle={`color: ${volatilityColor('high')}`}
                                            >High</Text
                                        >
                                        <Text bodyWeight="bold" customStyle={`color: #3EFFB4`}>$0.2 to $100</Text>
                                    </div>
                                </div>
                            </div>
						</div> -->

					{#if original}
						{#if isPreviewMode}
							{#if !hasDescription}
								<div class="review-content">
									<InfoCard width="100%" borderRadius="8px">
										<div class="review-cards-row">
											<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
											{#key reviewRefreshKey}
												<AverageRating {game} />
											{/key}
										</div>
									</InfoCard>
									<Comments
										gameSlug={toGameSlug(game.game)}
										providerSlug={game.provider_slug ?? ''}
									/>
								</div>
							{:else if activeTab === 'description'}
								{#if htmlContent}
									<div class="accordion-markdown">{@html htmlContent}</div>
								{/if}
							{:else if activeTab === 'review'}
								<div class="review-content">
									<InfoCard width="100%" borderRadius="8px">
										<div class="review-cards-row">
											<RateGame {game} on:reviewUpdated={handleReviewUpdate} />
											{#key reviewRefreshKey}
												<AverageRating {game} />
											{/key}
										</div>
									</InfoCard>
									<Comments
										gameSlug={toGameSlug(game.game)}
										providerSlug={game.provider_slug ?? ''}
									/>
								</div>
							{/if}
						{:else if hasDescription && htmlContent}
							<div class="accordion-markdown">{@html htmlContent}</div>
						{/if}
					{/if}
					<!-- {#if game.banner}
                        <img src={game.banner} alt={$t(Verbiage.icon_alt, 'alts')} class="game-banner" />
                    {/if} -->
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	:global(.info-accordion-game-name-text) {
		font-size: 1.125rem !important;
		font-weight: 600 !important;
		line-height: normal !important;
	}

	.pointer-events-none {
		pointer-events: none !important;
	}

	.custom-dropdown {
		position: relative;
		height: -moz-fit-content;
		height: fit-content;
		width: 100%;
		border-radius: 0.75rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		background: var(--gradient-main);
}

	.custom-dropdown-open {
		background: var(--gradient-alternative);
}

	.dropdown-toggle {
		position: relative;
		z-index: 10;
		display: block;
		height: 88px;
		width: 100%;
		border-radius: 0.5rem;
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		letter-spacing: 1.5px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.pointer {
		cursor: pointer;
}

	.dropdown-menu {
		position: relative;
		margin-top: -1.5rem;
		width: 100%;
		overflow: hidden;
		border-radius: 0.5rem;
		background-color: transparent;
		padding-left: 0px;
		padding-right: 0px;
		padding-top: 1.25rem;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		transition-property: all;
		transition-duration: 150ms;
		transition-duration: 500;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
		max-height: 0;
}

	.dropdown-menu.expanded {
		max-height: 10000px;
	}

	.d-flex-end {
		display: flex;
		width: 50%;
		align-items: center;
		justify-content: flex-end;
		gap: 0.5rem;
}

	.d-flex-col {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		text-align: start;
}

	.title-name {
		display: none;
		white-space: nowrap;
}

	@media (min-width: 768px) {

		.title-name {
				display: flex;
		}
}

	.title-name-mobile {
		display: flex;
		white-space: nowrap;
}

	@media (min-width: 768px) {

		.title-name-mobile {
				display: none;
		}
}

	.biggest-win {
		position: relative;
		z-index: 10;
		display: none;
		height: 56px;
		width: 100%;
		max-width: 280px;
		align-items: center;
		justify-content: space-between;
		padding: 1rem;
}

	@media (min-width: 1024px) {

		.biggest-win {
				display: flex;
		}
}

	.rotated {
		transform: rotate(180deg);
		transition: transform 0.3s ease;
	}

	.game-content {
		position: relative;
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		padding: 1rem;
}

	.bold-text {
		font-weight: 700;
}

	.provider-page-link {
		color: inherit;
		text-decoration-line: none;
}

	.provider-page-link:hover {
		text-decoration-line: underline;
		text-underline-offset: 2px;
}

	.game-description {
		position: relative;
		z-index: 30;
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	.game-banner {
		width: 100%;
}

	.description-badge {
		display: flex;
		height: 32px;
		width: 86px;
		align-items: center;
		justify-content: center;
		border-radius: 0.5rem;
		padding: 1rem;
		--tw-shadow-color: var(--shadow-neutral);
		--tw-shadow: var(--tw-shadow-colored);
		background: var(--gradient-main);
}

	.accordion-markdown {
		margin-bottom: 1rem;
		border-radius: 0.5rem;
		padding: 1rem;
}

	.no-description {
		padding-top: 2rem;
		padding-bottom: 2rem;
		text-align: center;
}

	.review-content {
		display: flex;
		flex-direction: column;
		gap: 1rem;
}

	.review-cards-row {
		display: flex;
		width: 100%;
		flex-direction: column;
		gap: 1rem;
}

	@media (min-width: 1024px) {

		.review-cards-row {
				flex-direction: row;
		}

		.review-cards-row > :global(*:first-child) {
				width: 30%;
		}

		.review-cards-row > :global(*:last-child) {
				flex: 1 1 0%;
		}
}

	:global(.accordion-markdown h1),
	:global(.accordion-markdown h2),
	:global(.accordion-markdown h3),
	:global(.accordion-markdown h4),
	:global(.accordion-markdown h5),
	:global(.accordion-markdown h6) {
		margin-bottom: 2rem;
		font-weight: 700;
}

	:global(.accordion-markdown h3) {
		font-size: 20px;
}

	:global(.accordion-markdown h1) {
		line-height: 28px;
}

	:global(.accordion-markdown h2) {
		line-height: 24px;
}

	:global(.accordion-markdown h3) {
		line-height: 22px;
}

	:global(.accordion-markdown h4) {
		line-height: 20px;
}

	:global(.accordion-markdown h5) {
		line-height: 18px;
}

	:global(.accordion-markdown h6) {
		line-height: 16px;
}

	:global(.accordion-markdown p),
	:global(.accordion-markdown ul),
	:global(.accordion-markdown ol) {
		margin-bottom: 2rem;
		font-size: 16px;
		font-weight: 100;
}

	:global(.accordion-markdown li) {
		margin-bottom: 1rem;
}

	:global(.accordion-markdown hr) {
		margin-bottom: 2rem;
		height: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-neutral-900);
}

	:global(.accordion-markdown table) {
		margin-bottom: 1rem;
		width: 100%;
		border-collapse: collapse;
		border-radius: 0.5rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
}

	:global(.accordion-markdown th),
	:global(.accordion-markdown td) {
		border-width: 1px;
		border-color: var(--color-neutral-900);
		padding-left: 1rem;
		padding-right: 1rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		background: var(--color-background-500);
}

	:global(.accordion-markdown th) {
		background: var(--color-background-500);
		font-size: 16px;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
	}

	:global(.accordion-markdown td) {
		font-size: 12px;
		font-weight: 100;
}

	.chevron-container {
		transition-property: transform;
		transition-duration: 100ms;
		transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

	.rotated {
		--tw-rotate: 180deg;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
}
</style>
